//check the given color is related to white
export function isWhite(str: string) {
  var whiteLimit = 200,
    r,
    g,
    b;

  r = parseInt("0x" + str.substring(1, 3));
  g = parseInt("0x" + str.substring(3, 5));
  b = parseInt("0x" + str.substring(5, 7));
  if (r < whiteLimit || b < whiteLimit || g < whiteLimit) {
    return false;
  }
  return true;
}

// Helper function to detect if URL is an Instagram Reel
export const isInstagramReel = (url: string): boolean => {
  return /instagram\.com\/reel\//i.test(url);
};

// Helper function to detect if URL is a regular Instagram post/video
export const isInstagramPost = (url: string): boolean => {
  return /instagram\.com\/p\//i.test(url);
};

// Helper function to detect if URL is any Instagram content (reel or post)
export const isInstagram = (url: string): boolean => {
  return isInstagramReel(url) || isInstagramPost(url);
};

// Helper function to convert Instagram Reel URL to embed format
export const getInstagramReelEmbedUrl = (url: string): string => {
  // Extract the reel ID from the URL
  const match = url.match(/instagram\.com\/reel\/([^/?]+)/i);
  if (match && match[1]) {
    return `https://www.instagram.com/reel/${match[1]}/embed/`;
  }
  return url;
};

// Helper function to convert Instagram post URL to embed format
export const getInstagramPostEmbedUrl = (url: string): string => {
  // Extract the post ID from the URL
  const match = url.match(/instagram\.com\/p\/([^/?]+)/i);
  if (match && match[1]) {
    return `https://www.instagram.com/p/${match[1]}/embed/`;
  }
  return url;
};

// Helper function to convert Instagram URL to embed format (handles both reels and posts)
export const getInstagramEmbedUrl = (url: string): string => {
  if (isInstagramReel(url)) {
    return getInstagramReelEmbedUrl(url);
  }
  if (isInstagramPost(url)) {
    return getInstagramPostEmbedUrl(url);
  }
  return url;
};

// Helper function to detect if URL is a Facebook Reel
export const isFacebookReel = (url: string): boolean => {
  return /facebook\.com\/reel\//i.test(url);
};

// Helper function to convert Facebook Reel URL to embed format
// Note: Facebook Reels may have embedding restrictions. Trying multiple formats.
export const getFacebookReelEmbedUrl = (url: string): string => {
  // Extract the reel ID from the URL
  const match = url.match(/facebook\.com\/reel\/([^/?]+)/i);
  if (match && match[1]) {
    // Try using the Facebook post embed format (some reels might work this way)
    const encodedUrl = encodeURIComponent(`https://www.facebook.com/reel/${match[1]}`);
    // Try the post embed format which sometimes works for reels
    return `https://www.facebook.com/plugins/post.php?href=${encodedUrl}&show_text=false&width=500`;
  }
  return url;
};

// Helper function to detect if URL is a Facebook video (non-reel)
export const isFacebookVideo = (url: string): boolean => {
  return /facebook\.com\/(.*\/)?videos\/|facebook\.com\/watch\/|facebook\.com\/share\/[vr]\/|fb\.watch\//i.test(url);
};

// Helper function to detect if URL is a Facebook share URL (new format)
export const isFacebookShareUrl = (url: string): boolean => {
  return /facebook\.com\/share\/[vr]\//i.test(url);
};

// Helper function to convert Facebook share URLs to embed-friendly format
export const getFacebookVideoEmbedUrl = (url: string): string => {
  // Check if it's a new share URL format (e.g., facebook.com/share/v/ or facebook.com/share/r/)
  const shareMatch = url.match(/facebook\.com\/share\/[vr]\/([^/?]+)/i);
  if (shareMatch && shareMatch[1]) {
    // For new share URLs, use Facebook's plugin embed format
    // This is more compatible than relying on react-social-media-embed support
    const encodedUrl = encodeURIComponent(url);
    return `https://www.facebook.com/plugins/video.php?href=${encodedUrl}&show_text=false&width=500`;
  }
  
  // Convert fb.watch URLs to facebook.com/watch format
  const fbWatchMatch = url.match(/fb\.watch\/([^/?]+)/i);
  if (fbWatchMatch && fbWatchMatch[1]) {
    return `https://www.facebook.com/watch/?v=${fbWatchMatch[1]}`;
  }
  
  return url;
};

// Helper function to detect if URL is a TikTok video
export const isTikTok = (url: string): boolean => {
  return /tiktok\.com\/@.+\/video\/|tiktok\.com\/t\/|vm\.tiktok\.com\/|vt\.tiktok\.com\//i.test(url);
};

// Helper function to detect if URL is a Twitter/X post
export const isTwitter = (url: string): boolean => {
  return /(twitter\.com|x\.com)\/.+\/status\/\d+/i.test(url);
};

// Helper function to detect if URL is a LinkedIn post
export const isLinkedIn = (url: string): boolean => {
  return /linkedin\.com\/(posts|feed\/update\/urn:li:activity)/i.test(url);
};

// Helper to convert LinkedIn post URL to embeddable URL
export const getLinkedInEmbedUrl = (url: string): string => {
  const activityMatch =
    url.match(/activity[-:](\d+)/i) || url.match(/urn:li:activity:(\d+)/i);
  if (activityMatch && activityMatch[1]) {
    return `https://www.linkedin.com/embed/feed/update/urn:li:activity:${activityMatch[1]}`;
  }
  return url;
};

// Helper function to detect if URL is a Pinterest pin
export const isPinterest = (url: string): boolean => {
  return /pinterest\.com\/pin\//i.test(url);
};

// Helper function to check if URL needs iframe embed handling (Instagram Reels, Instagram Posts)
// Note: Facebook Reels don't support iframe embedding reliably, so we use react-player for them
export const needsIframeEmbed = (url: string): boolean => {
  return isInstagram(url) || isTikTok(url) || isTwitter(url) || isLinkedIn(url) || isPinterest(url);
};

// Helper function to check if URL needs special embed handling (Instagram, Facebook, etc.)
export const needsSpecialEmbed = (url: string): boolean => {
  return (
    isInstagram(url) ||
    isFacebookReel(url) ||
    isFacebookVideo(url) ||
    isTikTok(url) ||
    isTwitter(url) ||
    isLinkedIn(url) ||
    isPinterest(url)
  );
};

// Helper function to get embed URL for platforms that need special handling
export const getEmbedUrl = (url: string): string => {
  if (isInstagram(url)) {
    return getInstagramEmbedUrl(url);
  }
  if (isTikTok(url)) {
    return url;
  }
  if (isTwitter(url) || isLinkedIn(url) || isPinterest(url)) {
    return url;
  }
  if (isFacebookReel(url)) {
    return getFacebookReelEmbedUrl(url);
  }
  // Facebook videos (non-reel) are handled by react-player, but we normalize the URL
  if (isFacebookVideo(url)) {
    // Normalize Facebook video URLs for better compatibility
    // Convert fb.watch URLs to facebook.com/watch format if needed
    const fbWatchMatch = url.match(/fb\.watch\/([^/?]+)/i);
    if (fbWatchMatch && fbWatchMatch[1]) {
      return `https://www.facebook.com/watch/?v=${fbWatchMatch[1]}`;
    }
    return url;
  }
  return url;
};